/**
 * Tree-view explorer for the share-view chrome's sidebar — mirrors the
 * workspace's FileExplorer pattern (features/workspace/ui/FileExplorer.tsx):
 *
 *   - Expand/collapse with chevrons; click a folder to toggle.
 *   - Click a file to open (sidebar mode: switches viewer in place).
 *   - Recursive indented rows, FileIcon per row.
 *   - Lazy children load per-folder; loaded children cached on the node.
 *
 * Differences from the workspace version: fetches via the share-link
 * /api/share-links/session/:sid/list endpoint (not /api/files), so the
 * listing is scope-gated to the share root by the backend and works
 * for anonymous + verified-email recipients. `.shared` / `.chain`
 * rows surface the same click-gate dialog the full folder view uses.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SHARE_ROW_REFUSED_EVENT } from './SharedFolderView'
import { ChevronRight, ChevronDown, Loader2, Pencil, Trash2, ExternalLink, Download, Copy, Scissors, ClipboardPaste, FolderPlus, FilePlus, Upload, FolderUp, FolderTree, RotateCcw } from 'lucide-react'

import { FileIcon, fileIconProps, useContextMenu, useConfirm, useToast, useToastOptional, FileContextMenu, type FileMenuItem, miniappIconProps} from '@papan/core-ui/ui'
import type { SharedListItem, SharedListResponse } from './types'
import { sessionIdentityKey } from './sessionToken'
import { fetchServeToken, shareServeUrl, dbOperationNoticeKey, dbOperationNoticeTone } from '@papan/collab/client'
import { watchShareReplace } from './watchShareReplace'
import { getApiBase, isSessionEndedError, sessionFetch, currentSessionSignal, sessionEndedSince } from '@papan/api-client'   // #1316: an answer after the share session ended is dropped
import { isMiniappFolder, isPointerRow, TOO_LARGE_FOR_SHARE_VIEW_CODE, isContentDamagedCode } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { ShareRestoreTargetPicker } from './ShareRestoreTargetPicker'
import { shareWriteFetch, declinedDisclosure } from './AttributionDisclosure'
import { FolderLinkedDownloadDialog } from '@papan/files/ui/pointer-download'
import { restoreShareBinEntry } from './shareBinRestore'
import { sessionSignal, lastEndMovedShareSession } from '@papan/core-ui/ui/sessionEnded'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import { PointerNeedsApprovalDialog } from '@papan/files/ui/pointer-approval'
import { usePartitionGate, partitionRefusalKey, deleteResultMessage, moveResultMessage, type PartitionOp } from '@papan/files/ui/protected-branches'
import type { PartitionPreview } from '@papan/types'

interface Props {
  sessionId: string
  rootFileId: string
  themeClass: string
  /** Fires when the recipient clicks a leaf file. The chrome mints a
   *  sub-session and navigates in place. */
  onFileOpen: (fileId: string) => void
  /** Currently-open file id — highlighted in the tree. */
  activeFileId: string
  /** A rename landed — fires with the row's id and its new name, so the chrome can
   *  refresh the open file when it is the renamed one (the tree patch alone leaves
   *  the header / serve URLs on the old name). */
  onFileRenamed?: (fileId: string, name: string) => void
  /** Add-to-Drive handler for the `.shared` click-gate dialog. */
  onAddToDrive: () => void
  addState: 'idle' | 'busy' | 'success' | 'error'
  addMessage: string | null
  /**
   * Edit links may MANAGE files in the shared subtree (rename / delete) via a
   * right-click menu — same actions as the Drive explorer, minus the owner-only
   * governance ones (AI access, who-has-access, reveal-in-drive, link folder).
   * View links get no context menu. Default false.
   */
  canEdit?: boolean
  /**
   * (#757) Enter PDApp mode for a nested `.pdapp` folder — clicking one opens it
   * AS AN APP (the chrome's `?pdapp=`), the same as the folder listing does,
   * instead of expanding its internals in this tree. Absent ⇒ a PDApp expands
   * like any folder (e.g. when this tree is ALREADY rooted at the PDApp).
   */
  onOpenPdapp?: (folderId: string) => void
}

interface BinRow {
  id: string
  name: string
  isFolder: boolean
  deletedAt: string
}

interface TreeNode {
  id: string
  name: string
  isFolder: boolean
  fileType?: string | null
  /** Folder row's metadata_json — carries the miniappIcon spec. */
  metadataJson?: string | null
  sharedFromName?: string
  /** A DEAD pointer (the drive's rule, as the link resolves it): greyed, not openable. */
  chainBroken?: boolean
  shareBroken?: boolean
  shareNoAccess?: boolean
  /** D17: a `.chain` still needing approval — drawn uncoloured, in the theme's text colour (`fileIconProps`). */
  chainNeedsApproval?: boolean
  /** This node's EFFECTIVE permission (guarded max(native, link)) — gates the
   *  node's own write actions (rename/copy/cut/delete) and, for a folder,
   *  create-inside. */
  effectivePermission?: string
  /** Parent folder id, or null at the session root — for "create/paste alongside a file". */
  parentId: string | null
  children?: TreeNode[]
  loaded: boolean
  loading: boolean
}

/** Write-capable levels — mirrors @papan/database canWrite. */
/**
 * The server's storage-cap sentence from a failed response, or null.
 *
 * The share-session write routes answer a full drive with 413 +
 * `STORAGE_QUOTA_EXCEEDED` and the same sentence every other surface shows.
 * This view discarded the body on `!res.ok`, so that translation reached
 * nobody and a recipient filling the OWNER's drive saw "Copy failed" —
 * indistinguishable from a permission or network error, and useless on a drive
 * they cannot inspect.
 *
 * Reads the body defensively: a non-JSON error page must degrade to the
 * caller's own wording, never throw inside an error handler.
 */
async function storageRefusalFrom(res: Response): Promise<string | null> {
  try {
    const body = await res.clone().json() as { code?: string; error?: string }
    return body?.code === 'STORAGE_QUOTA_EXCEEDED' && body.error ? body.error : null
  } catch {
    return null
  }
}

/** An error answer's `code` / `landing` (cloned, defensively: a non-JSON body is `null`). */
async function answerOf(res: Response): Promise<{ code?: string; landing?: boolean; recordId?: string } | null> {
  try { return await res.clone().json() as { code?: string; landing?: boolean; recordId?: string } } catch { return null }
}
/** #1134 — an upload refused because a save of the db is still being written: sent again this often, this many times
 *  (the server hints 5 s; a landing normally takes seconds). */
const LANDING_RETRY_MS = 5_000
const LANDING_RETRIES = 3

/** Owner D145 — the server refused a RESERVED drive name (400 RESERVED_NAME): true so the caller says it in the
 *  visitor's language (`driveView.toast.reservedName`). */
async function isReservedNameRefusal(res: Response): Promise<boolean> {
  return (await answerOf(res))?.code === 'RESERVED_NAME'
}

/** `storageRefusalFrom`, falling back to the caller's generic wording. */
async function refusalOrDefault(res: Response, fallback: string): Promise<string> {
  return (await storageRefusalFrom(res)) ?? fallback
}

function canWritePerm(p: string | undefined): boolean {
  return p === 'readwrite' || p === 'admin' || p === 'owner'
}

function itemsToNodes(items: SharedListItem[], parentId: string): TreeNode[] {
  return items.map(f => ({
    id: f.id,
    name: f.name,
    isFolder: f.isFolder,
    fileType: f.fileType ?? null,
    metadataJson: f.metadataJson ?? null,
    sharedFromName: f.sharedFromName,
    ...(f.chainBroken ? { chainBroken: true } : {}),
    ...(f.shareBroken ? { shareBroken: true } : {}),
    ...(f.shareNoAccess ? { shareNoAccess: true } : {}),
    ...(f.chainNeedsApproval ? { chainNeedsApproval: true } : {}),
    effectivePermission: f.effectivePermission,
    parentId,
    children: f.isFolder ? [] : undefined,
    loaded: false,
    loading: false,
  }))
}

// Pick a non-colliding default name within a set of sibling names (mirrors the
// Drive's "New Folder", "New Folder 2", … behaviour so create-then-rename works).
function uniqueName(base: string, ext: string, siblings: string[]): string {
  const taken = new Set(siblings)
  let candidate = `${base}${ext}`
  let n = 2
  while (taken.has(candidate)) candidate = `${base} ${n++}${ext}`
  return candidate
}

function findNode(items: TreeNode[], id: string): TreeNode | null {
  for (const item of items) {
    if (item.id === id) return item
    if (item.children) {
      const found = findNode(item.children, id)
      if (found) return found
    }
  }
  return null
}

function updateNode(items: TreeNode[], id: string, updates: Partial<TreeNode>): TreeNode[] {
  return items.map(item => {
    if (item.id === id) return { ...item, ...updates }
    if (item.children) return { ...item, children: updateNode(item.children, id, updates) }
    return item
  })
}

function removeNode(items: TreeNode[], id: string): TreeNode[] {
  return items
    .filter(item => item.id !== id)
    .map(item => (item.children ? { ...item, children: removeNode(item.children, id) } : item))
}

function TreeRow({
  node, depth, activeFileId, expandedSet, onToggleExpand, onFileOpen, onLoad, onOpenPdapp,
  onContextMenu, renamingId, renameDraft, onRenameDraft, onRenameCommit, onRenameCancel,
}: {
  node: TreeNode
  depth: number
  activeFileId: string
  expandedSet: Set<string>
  onToggleExpand: (folderId: string) => void
  onFileOpen: (fileId: string) => void
  onLoad: (folderId: string) => void
  onOpenPdapp?: (folderId: string) => void
  onContextMenu: (node: TreeNode, e: React.MouseEvent) => void
  renamingId: string | null
  renameDraft: string
  onRenameDraft: (v: string) => void
  onRenameCommit: () => void
  onRenameCancel: () => void
}) {
  const isMount = isPointerRow(node)
  const broken = !!(node.shareBroken || node.chainBroken)
  const isActive = node.id === activeFileId
  const expanded = expandedSet.has(node.id)
  const isRenaming = renamingId === node.id
  const toast = useToastOptional()
  const { t } = useTranslation('files')
  const [pendingPointerInfo, setPendingPointerInfo] = useState(false)

  const handleClick = () => {
    // A DEAD pointer is greyed and not openable — the drive's words (owner ruling 2026-09-27).
    if (node.shareBroken) {
      toast?.info(node.shareNoAccess ? t('shareView.deadPointer.sharedNoAccessToast') : t('shareView.deadPointer.sharedRevokedToast'))
      return
    }
    if (node.chainBroken) { toast?.info(t('shareView.deadPointer.chainGoneToast')); return }
    // D17 — a `.chain` that Needs approval doesn't open, as in the drive (no Approve here: the reason).
    if (node.chainNeedsApproval) { setPendingPointerInfo(true); return }
    // (#757) A native nested PDApp opens as an app (PDApp mode), not as a
    // browsable folder. A `.shared`/`.chain` mount of one keeps #188's browse.
    if (onOpenPdapp && node.isFolder && !isMount && isMiniappFolder(node.name)) {
      onOpenPdapp(node.id)
      return
    }
    // #188 — a `.shared`/`.chain` mount presents as a folder; EXPAND it to browse its
    // resharer-capped reshared view (the /list nested-mount traversal) instead of the old
    // "add to Drive" dialog. Deeper compound children navigate the same way.
    if (isMount || node.isFolder) {
      onToggleExpand(node.id)
      // Trigger a lazy load when expanding for the first time. The
      // parent's `onLoad` is a no-op if children are already loaded,
      // so the call is cheap even on repeat expands.
      if (!expanded) onLoad(node.id)
      return
    }
    onFileOpen(node.id)
  }

  return (
    <>
      {pendingPointerInfo && <PointerNeedsApprovalDialog fileName={node.name} isFolder={node.isFolder} onClose={() => setPendingPointerInfo(false)} />}
      <button
        type="button"
        data-node-id={node.id}
        onClick={handleClick}
        onContextMenu={(e) => onContextMenu(node, e)}
        className={`flex items-center gap-1 w-full text-left themed-text-secondary hover:themed-text hover:themed-bg-tertiary py-1 px-1 rounded text-sm ${
          isActive ? 'themed-bg-tertiary themed-text' : ''
        } ${broken ? 'opacity-50' : ''}`}
        title={broken ? (node.shareNoAccess ? t('shareView.deadPointer.noAccess') : t('shareView.deadPointer.noLongerAvailable')) : undefined}
        {...(broken ? { 'data-broken': 'true' } : {})}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        {node.isFolder ? (
          node.loading ? (
            <Loader2 size={14} className="animate-spin shrink-0" />
          ) : expanded ? (
            <ChevronDown size={14} className="shrink-0" />
          ) : (
            <ChevronRight size={14} className="shrink-0" />
          )
        ) : (
          <span className="w-[14px] shrink-0" />
        )}
        <FileIcon
          {...fileIconProps(node)}
          size={14}
          {...miniappIconProps(node)}
        />
        {isRenaming ? (
          <input
            autoFocus
            data-testid="share-explorer-rename-input"
            value={renameDraft}
            onChange={(e) => onRenameDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter') onRenameCommit()
              else if (e.key === 'Escape') onRenameCancel()
            }}
            onBlur={onRenameCommit}
            className="flex-1 min-w-0 themed-input px-1 py-0 text-sm rounded"
          />
        ) : (
          <span className="truncate">{node.name}</span>
        )}
      </button>
      {node.isFolder && expanded && node.children && (
        <div>
          {node.children.map(child => (
            <TreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              activeFileId={activeFileId}
              expandedSet={expandedSet}
              onToggleExpand={onToggleExpand}
              onFileOpen={onFileOpen}
              onLoad={onLoad}
              onOpenPdapp={onOpenPdapp}
              onContextMenu={onContextMenu}
              renamingId={renamingId}
              renameDraft={renameDraft}
              onRenameDraft={onRenameDraft}
              onRenameCommit={onRenameCommit}
              onRenameCancel={onRenameCancel}
            />
          ))}
        </div>
      )}
    </>
  )
}

export function ShareFileExplorer({
  sessionId, rootFileId, themeClass, onFileOpen, activeFileId,
  canEdit = false, onOpenPdapp, onFileRenamed,
}: Props) {
  // Expanded-folder state survives navigation between sub-files in the
  // same share via localStorage. The chrome re-mounts on every
  // /share-view/<sid> URL change (each file click navigates to a new
  // sub-session), so component-local `expanded` state would reset.
  // Key by sessionId — derived per-share for now; if recipients open
  // the same share in multiple tabs the most-recent state wins on
  // next mount, which matches the workspace explorer's "single
  // remembered open-set per layout" feel.
  const expandedLsKey = useMemo(() => `share-view.tree-expanded.${rootFileId}`, [rootFileId])
  const [expandedSet, setExpandedSet] = useState<Set<string>>(() => {
    try {
      const raw = window.localStorage.getItem(expandedLsKey)
      if (raw) return new Set(JSON.parse(raw) as string[])
    } catch { /* ignore */ }
    return new Set()
  })
  useEffect(() => {
    try { window.localStorage.setItem(expandedLsKey, JSON.stringify([...expandedSet])) } catch { /* ignore */ }
  }, [expandedLsKey, expandedSet])

  const toggleExpand = useCallback((id: string) => {
    setExpandedSet(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const [rootItems, setRootItems] = useState<TreeNode[]>([])
  // The share-root folder's own effective permission (for create-inside/paste/
  // upload at the root, and the empty-space menu). Child folders carry their own
  // `effectivePermission`; only the root isn't a child of any listing.
  const [rootPermission, setRootPermission] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  // Right-click file management (edit links only). Reuses the shared
  // useContextMenu / useConfirm / useToast primitives — no bespoke menu infra.
  const { menu, menuRef, open: openMenu, close: closeMenu } = useContextMenu<{ x: number; y: number; node: TreeNode | null }>()
  const confirm = useConfirm()
  const toast = useToast()
  const { t } = useTranslation('files')
  const { t: tCollab } = useTranslation('collab')
  // #1090 — background Replaces this view started are watched while it is mounted (the loops stop when it goes).
  const replaceWatchAliveRef = useRef(true)
  useEffect(() => { replaceWatchAliveRef.current = true; return () => { replaceWatchAliveRef.current = false } }, [])
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const renameCommitting = useRef(false)
  // Copy/Cut clipboard (within this share). Paste copies (POST /copy) or moves
  // (PATCH parentId) into the target folder.
  const [clipboard, setClipboard] = useState<{ id: string; name: string; isFolder: boolean; mode: 'copy' | 'cut' } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const uploadTargetRef = useRef<string>(rootFileId)
  // The share-session token changes on every file switch (each open mints a new
  // per-file sub-session), but the folder LISTING is identical — only the auth
  // credential differs. Read the live token through a ref so `loadFolder` stays
  // referentially stable and the initial-load effect does NOT re-run (which would
  // collapse + rebuild the whole tree — the visible "tree blink" on each open).
  // Writes/mints below still use the live `sessionId` prop directly.
  const sessionIdRef = useRef(sessionId)
  sessionIdRef.current = sessionId
  // The RECIPIENT identity encoded in the token (verified email + active tenant).
  // A plain file switch mints a new token with the SAME identity → the tree stays
  // put (loadFolder reads the live token via the ref, so it doesn't re-run). An
  // identity switch (bind-account → another tenant, or a different recipient)
  // changes this key → the initial-load effect re-runs so the per-file
  // effectivePermission the tree renders reflects the NEW identity, not the old.
  const identityKey = useMemo(() => sessionIdentityKey(sessionId), [sessionId])

  const handleContextMenu = useCallback((node: TreeNode, e: React.MouseEvent) => {
    // View links now get the SAME menu — the read actions (Open / Download) work, and
    // the write actions render disabled (gated by `canEdit` in the item list below).
    // Cross-tenant mounts still get no menu (they can't be managed through this token;
    // the backend rejects either way).
    if (isPointerRow(node)) return
    e.preventDefault()
    e.stopPropagation()
    openMenu({ x: e.clientX, y: e.clientY, node })
  }, [openMenu])

  // Right-click on empty explorer space → background menu (target = share root).
  // Gate on the root's OWN effective perm (not the tree-wide canEdit): a recipient
  // whose native write is confined to a subfile shouldn't get an all-disabled menu
  // at the read-only root. The item list gates each action on roScope anyway.
  const handleBackgroundContextMenu = useCallback((e: React.MouseEvent) => {
    if (!canWritePerm(rootPermission)) return
    e.preventDefault()
    openMenu({ x: e.clientX, y: e.clientY, node: null })
  }, [rootPermission, openMenu])

  const startRename = useCallback((node: TreeNode) => {
    closeMenu()
    setRenameDraft(node.name)
    setRenamingId(node.id)
  }, [closeMenu])

  const cancelRename = useCallback(() => {
    setRenamingId(null)
    setRenameDraft('')
  }, [])

  // F2 renames the focused row — the "F2" hint shown in the context menu was
  // never wired to a key handler. Targets whichever row button holds focus (its
  // id lives in `data-node-id`), gated by that row's OWN effective permission,
  // so it can only start a rename where Rename is enabled in the menu. Skips
  // cross-tenant mounts (renamed through this token is rejected server-side) and
  // never fires while a text field (incl. the rename input) has focus.
  // The listener is registered ONCE and reads the latest list / rename state / startRename through refs set during
  // render: re-registering it from an effect on every change left a window — after a new list was painted, before the
  // effect ran — in which an F2 reached the previous listener (its list still empty) and was ignored (main CI flake,
  // 2026-10-04).
  const f2Ref = useRef({ rootItems, renamingId, startRename })
  f2Ref.current = { rootItems, renamingId, startRename }
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const { rootItems: items, renamingId: renaming, startRename: start } = f2Ref.current
      if (e.key !== 'F2' || renaming) return
      const el = document.activeElement as HTMLElement | null
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return
      const id = el?.dataset?.nodeId
      if (!id) return
      const node = findNode(items, id)
      if (!node || isPointerRow(node)) return
      if (!canWritePerm(node.effectivePermission)) return
      e.preventDefault()
      start(node)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  // #1264 — an action on a listed row refused with 403 / 404: the row may be gone (its delete never
  // announced — this view has no realtime anyway), or not this visitor's to change; the server answers
  // both alike. Re-read the folder that lists it: a gone row drops, anything else stays. Through a ref:
  // `reloadFolder` is declared below.
  const reloadFolderRef = useRef<(folderId: string, strict?: boolean, expand?: boolean) => Promise<void>>(async () => {})
  const rowRefusedRef = useRef<(status: number, rowId: string) => void>(() => {})
  const rootItemsRef = useRef(rootItems)
  rootItemsRef.current = rootItems
  rowRefusedRef.current = (status, rowId) => {
    if (status !== 403 && status !== 404) return
    const row = findNode(rootItems, rowId)
    // strict: a failed re-read leaves the tree as it is (never replaced with nothing)
    void reloadFolderRef.current(row?.parentId ?? rootFileId, true, false).catch(() => {})
  }

  const commitRename = useCallback(async () => {
    if (renameCommitting.current) return
    const id = renamingId
    const next = renameDraft.trim()
    if (!id) return
    const node = findNode(rootItems, id)
    if (!node || !next || next === node.name) { cancelRename(); return }
    renameCommitting.current = true
    try {
      const res = await shareWriteFetch(sessionId, `${getApiBase()}/api/share-links/session/${sessionId}/file/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: next }),
      })
      if (declinedDisclosure(res)) return
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string }
        toast.error(body.code === 'RESERVED_NAME' ? t('driveView.toast.reservedName') : body.error === 'invalid_name' ? 'Invalid name' : 'Rename failed')
        rowRefusedRef.current(res.status, id)
        return
      }
      const body = (await res.json()) as { file?: { name?: string } }
      const newName = body.file?.name ?? next
      setRootItems(prev => updateNode(prev, id, { name: newName }))
      onFileRenamed?.(id, newName)
    } catch {
      toast.error('Rename failed')
    } finally {
      renameCommitting.current = false
      cancelRename()
    }
  }, [renamingId, renameDraft, rootItems, sessionId, toast, cancelRename, onFileRenamed])

  // Mint a per-file sub-session so Open/Download serve relative to a root that
  // actually contains the file. The explorer builds node paths relative to the
  // SHARE root, but the current session's /s/ serve-root can be a subfolder
  // (a folder share opened at a sub-file) — so a raw /s/<thisSession>/<path>
  // 404s. A fresh sub-session is scoped to the file (or its folder for HTML),
  // so /s/<sub>/<name> resolves in every case.
  const mintFileSubSession = useCallback(async (fileId: string): Promise<string | null> => {
    try {
      const res = await sessionFetch(`${getApiBase()}/api/share-links/session/${sessionId}/sub-session`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId }),
      })
      if (!res.ok) { rowRefusedRef.current(res.status, fileId); return null }
      return ((await res.json()) as { sessionId: string }).sessionId
    } catch { return null }
  }, [sessionId])

  const handleOpenNewTab = useCallback(async (node: TreeNode) => {
    closeMenu()
    // Reserve the tab synchronously (inside the click gesture) so the popup
    // blocker doesn't kill it after the async sub-session mint; point it once
    // the sub-session is ready.
    const w = window.open('', '_blank')
    const sub = await mintFileSubSession(node.id)
    if (!w) return
    if (sub) w.location.href = `/share-view/${sub}`
    else { w.close(); toast.error('Could not open file') }
  }, [closeMenu, mintFileSubSession, toast])

  const handleDownload = useCallback(async (node: TreeNode) => {
    closeMenu()
    const sub = await mintFileSubSession(node.id)
    if (!sub) { toast.error('Download failed'); return }
    // #989 D3: the preview host never sees the browser-binding cookie — the download URL
    // carries the sub-session's SERVE token, never the session.
    const serve = await fetchServeToken(sub)
    if (serve === 'ended') return   // #1316: its session ended — no download, no error for the next session
    if (serve === 'fallback' || serve === 'retry') { toast.error('Download failed'); return }
    // <a download> click — the serve route streams the bytes, the `download`
    // attr makes the browser save them. Name is relative to the sub-session
    // serve-root, which contains the file.
    const a = document.createElement('a')
    // Preview origin (#235): cross-origin `download` attr is ignored by the
    // browser, but the serve route's Content-Disposition (?download=1) forces
    // the save dialog with the right filename anyway.
    a.href = shareServeUrl(serve, encodeURIComponent(node.name), 'download=1')
    a.download = node.name
    document.body.appendChild(a)
    a.click()
    a.remove()
  }, [closeMenu, mintFileSubSession, toast])

  // A folder holding `.shared` / `.chain` asks first — pointer FILES or their CONTENTS —
  // with the drive's own dialog (FolderLinkedDownloadDialog); the zip is built by the
  // same server code as the drive's.
  const [linkedDownload, setLinkedDownload] = useState<TreeNode | null>(null)
  const startFolderDownload = useCallback((node: TreeNode, expandPointers: boolean) => {
    // The download-folder endpoint streams a zip of the subtree; it takes the
    // folder id directly (scoped to the share root), so no sub-session needed.
    const a = document.createElement('a')
    // -api origin (#234): the folder-zip download needs the recipient's session
    // cookie, which is host-scoped to the API origin.
    a.href = `${getApiBase()}/api/share-links/session/${sessionId}/download-folder?folderId=${encodeURIComponent(node.id)}${expandPointers ? '&expandPointers=1' : ''}`
    a.download = `${node.name}.zip`
    document.body.appendChild(a)
    a.click()
    a.remove()
  }, [sessionId])
  const handleDownloadFolder = useCallback(async (node: TreeNode) => {
    closeMenu()
    try {
      const res = await sessionFetch(`${getApiBase()}/api/share-links/session/${sessionId}/download-folder?folderId=${encodeURIComponent(node.id)}&anyPointers=1`, { credentials: 'include' })
      const body = res.ok ? (await res.json()) as { anyPointers?: boolean } : null
      if (body?.anyPointers) { setLinkedDownload(node); return }
    } catch { /* fall through to a plain download */ }
    // No question asked (or the pre-flight failed): the share view's default is CONTENTS
    // (owner ruling 2026-09-27) — as ShareViewerChrome's zip — never pointer files the
    // visitor wasn't asked about.
    startFolderDownload(node, true)
  }, [startFolderDownload, closeMenu, sessionId])

  // #801 — the protected-branch preview, through the SESSION's own route (the visitor's
  // stored identity, as the listing shows it). A non-OK answer throws its body's fields,
  // so `partitionRefusalKey` reads `error: 'protected_branches'` + flags alike.
  const partitionGate = usePartitionGate()
  // A MOVE may name its destination (`dest`): the session route then also runs the move's own
  // destination gate and reports a refusal as `blocked` (L4 round 6, M-17c).
  const loadPreview = useCallback(async (fileId: string, op: PartitionOp, dest?: string): Promise<PartitionPreview> => {
    const d = op === 'move' && dest ? `&dest=${encodeURIComponent(dest)}` : ''
    const res = await sessionFetch(`${getApiBase()}/api/share-links/session/${sessionId}/file/${encodeURIComponent(fileId)}/protected-branches?op=${op}${d}`, { credentials: 'include' })
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
    if (!res.ok) throw Object.assign(new Error(typeof body.error === 'string' ? body.error : 'preview_failed'), { status: res.status, ...body })
    return body as unknown as PartitionPreview
  }, [sessionId])

  const handleDelete = useCallback(async (node: TreeNode) => {
    closeMenu()
    // #801: a folder is previewed first — refused whole with an info modal, or
    // confirmed with the preview (replacing the plain confirm) and deleted with the
    // previewed set. Nothing protected: the plain confirm below.
    let expectRemovable: string[] | undefined
    if (node.isFolder) {
      const g = await partitionGate.gate(node, 'delete', () => loadPreview(node.id, 'delete'), { deleteTitle: 'Delete folder' })
      if (!g.go) return
      if (g.confirmed) expectRemovable = g.expectRemovable
    }
    const ok = expectRemovable !== undefined || await confirm({
      title: node.isFolder ? 'Delete folder' : 'Delete file',
      message: `Delete "${node.name}"? This removes it from the shared drive.`,
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!ok) return
    try {
      const res = await shareWriteFetch(sessionId, `${getApiBase()}/api/share-links/session/${sessionId}/file/${encodeURIComponent(node.id)}`, {
        method: 'DELETE',
        credentials: 'include',
        ...(expectRemovable ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectRemovable }) } : {}),
      })
      if (declinedDisclosure(res)) return
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { code?: string; min?: number }
        // #854 D12: too big to delete from a link — say so, and who can. #801: a
        // folder refused whole, or changed since its preview.
        const refusal = partitionRefusalKey(body, 'delete')
        toast.error(body.code === TOO_LARGE_FOR_SHARE_VIEW_CODE
          ? t('shareView.tooLargeToDelete', { name: node.name, min: (body.min ?? 0).toLocaleString() })
          : refusal ? t(refusal) : 'Delete failed')
        rowRefusedRef.current(res.status, node.id)
        return
      }
      // #801: a SPLIT delete leaves the folder and the branches this visitor may not change
      // in place — remove only what actually went to the bin. A whole delete (or an older
      // server's bare `{ ok }`) removes the folder itself.
      const body = (await res.json().catch(() => null)) as { binId?: string; binIds?: string[]; binnedFileIds?: string[]; skipped?: PartitionPreview } | null
      const whole = !body?.skipped && (body?.binId !== undefined || body?.binnedFileIds === undefined)
      setRootItems(prev => whole ? removeNode(prev, node.id) : (body?.binnedFileIds ?? []).reduce(removeNode, prev))
      // A split names what was skipped; a delete that binned nothing says so.
      const msg = body ? deleteResultMessage(t, body) : null
      if (msg) toast.info(msg)
    } catch {
      toast.error('Delete failed')
    }
  }, [closeMenu, confirm, sessionId, toast, t, partitionGate, loadPreview])

  const loadFolder = useCallback(async (parentId: string, strict = false): Promise<TreeNode[]> => {
    const res = await sessionFetch(
      `${getApiBase()}/api/share-links/session/${sessionIdRef.current}/list?parentId=${encodeURIComponent(parentId)}`,
      { credentials: 'include' },
    )
    // `strict` (#1264's refresh): a failed read throws, so the tree is never replaced with nothing
    if (!res.ok) { if (strict) throw Object.assign(new Error('list_failed'), { status: res.status }); return [] }
    const body = (await res.json()) as SharedListResponse
    if (parentId === rootFileId && body.parentPermission) setRootPermission(body.parentPermission)
    return itemsToNodes(body.files, parentId)
  }, [rootFileId])

  const handleLoad = useCallback(async (folderId: string) => {
    const node = findNode(rootItems, folderId)
    if (!node || !node.isFolder || node.loaded) return
    setRootItems(prev => updateNode(prev, folderId, { loading: true }))
    try {
      const children = await loadFolder(folderId, true)
      setRootItems(prev => updateNode(prev, folderId, { children, loaded: true, loading: false }))
    } catch (err) {
      setRootItems(prev => updateNode(prev, folderId, { loading: false }))
      // #1316: the listing was asked by a session that has since ended (dropped). The tree outlives a share-session
      // replacement of the same identity (it rebuilds only on a root / identity change), so leave the folder
      // collapsed and not loaded: the next click expands it and asks again, as the session now here.
      if (isSessionEndedError(err)) {
        setExpandedSet(prev => { if (!prev.has(folderId)) return prev; const next = new Set(prev); next.delete(folderId); return next })
        return
      }
      // #1264: a folder that 403/404s on expand may be gone — its parent re-reads (the row drops)
      const status = (err as { status?: number } | null)?.status
      if (status === 403 || status === 404) rowRefusedRef.current(status, folderId)
    }
  }, [rootItems, loadFolder])

  // Re-fetch a folder's children after a write, preserving already-loaded
  // subtrees of unchanged child folders so the tree doesn't collapse.
  const mergeLoaded = (fresh: TreeNode[], existing: TreeNode[] | undefined): TreeNode[] =>
    fresh.map(f => {
      const old = existing?.find(e => e.id === f.id)
      return old && old.loaded ? { ...f, children: old.children, loaded: true } : f
    })

  // `strict` (#1264's refresh): a failed read throws instead of emptying the folder; `expand: false`
  // re-reads without opening a collapsed folder. Merged into the tree AS IT IS NOW (`prev`), never a
  // render-time snapshot — two re-reads landing out of order can't put a dropped row back.
  const reloadFolder = useCallback(async (folderId: string, strict = false, expand = true) => {
    if (folderId === rootFileId) {
      const fresh = await loadFolder(rootFileId, strict)
      setRootItems(prev => mergeLoaded(fresh, prev))
      return
    }
    const fresh = await loadFolder(folderId, strict)
    setRootItems(prev => updateNode(prev, folderId, { children: mergeLoaded(fresh, findNode(prev, folderId)?.children), loaded: true }))
    if (expand) setExpandedSet(prev => (prev.has(folderId) ? prev : new Set(prev).add(folderId)))
  }, [rootFileId, loadFolder])
  reloadFolderRef.current = reloadFolder
  // …and when the chrome could not open one of this tree's rows (it may be gone): the same re-read.
  useEffect(() => {
    const onRefused = (e: Event) => {
      const fileId = (e as CustomEvent<{ fileId?: string }>).detail?.fileId
      if (fileId && findNode(rootItemsRef.current, fileId)) rowRefusedRef.current(403, fileId)
    }
    window.addEventListener(SHARE_ROW_REFUSED_EVENT, onRefused)
    return () => window.removeEventListener(SHARE_ROW_REFUSED_EVENT, onRefused)
  }, [])

  // Destination folder for create / paste / upload: a folder node → itself, a
  // file node → its parent, the background → the share root.
  const targetFolderOf = useCallback((node: TreeNode | null): string =>
    node ? (node.isFolder ? node.id : (node.parentId ?? rootFileId)) : rootFileId, [rootFileId])

  // The effective permission on a folder id — the root's own perm for the share
  // root, else the folder node's `effectivePermission` (carried from its parent's
  // listing). Gates create-inside / paste / upload into that folder.
  const folderPermOf = useCallback((folderId: string): string | undefined =>
    folderId === rootFileId ? rootPermission : findNode(rootItems, folderId)?.effectivePermission,
    [rootFileId, rootPermission, rootItems])

  const siblingNames = useCallback((folderId: string): string[] => {
    if (folderId === rootFileId) return rootItems.map(n => n.name)
    return (findNode(rootItems, folderId)?.children ?? []).map(n => n.name)
  }, [rootItems, rootFileId])

  const handleCopyCut = useCallback((node: TreeNode, mode: 'copy' | 'cut') => {
    closeMenu()
    setClipboard({ id: node.id, name: node.name, isFolder: node.isFolder, mode })
  }, [closeMenu])

  // A copy within the share stays in ONE drive: pointers are kept, nothing is asked (owner
  // ruling 2026-09-27 — the file-or-contents choice is for copies that cross tenants).
  const handlePaste = useCallback(async (node: TreeNode | null) => {
    closeMenu()
    if (!clipboard) return
    const target = targetFolderOf(node)
    // #801: a folder CUT is previewed first — refused whole (info), or confirmed with the
    // preview and moved with the previewed set. A copy carries nothing away.
    let expectRemovable: string[] | undefined
    if (clipboard.mode === 'cut' && clipboard.isFolder) {
      const g = await partitionGate.gate(clipboard, 'move', () => loadPreview(clipboard.id, 'move', target))
      if (!g.go) return
      if (g.confirmed) expectRemovable = g.expectRemovable
    }
    try {
      const res = clipboard.mode === 'copy'
        ? await shareWriteFetch(sessionId, `${getApiBase()}/api/share-links/session/${sessionId}/copy`, {
            method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileId: clipboard.id, destParentId: target }),
          })
        : await shareWriteFetch(sessionId, `${getApiBase()}/api/share-links/session/${sessionId}/file/${encodeURIComponent(clipboard.id)}`, {
            method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ parentId: target, ...(expectRemovable ? { expectRemovable } : {}) }),
          })
      if (declinedDisclosure(res)) return
      if (!res.ok) {
        // #801: a partition refusal names itself (read from a clone — the storage
        // refusal below reads the body too).
        const refusal = clipboard.mode === 'cut' ? partitionRefusalKey(await res.clone().json().catch(() => null), 'move') : null
        if (refusal) { toast.error(t(refusal)); return }
        // #922 S3: a copy too big to run inline from a link (the 413 the share-view delete
        // answers too) — say so and what to do instead. A full drive's 413 falls through.
        if (clipboard.mode === 'copy') {
          const big = (await res.clone().json().catch(() => null)) as { code?: string; min?: number; name?: string; companion?: string } | null
          if (big?.code === TOO_LARGE_FOR_SHARE_VIEW_CODE) {
            toast.error(t('shareView.tooLargeToCopy', { name: clipboard.name, min: (big.min ?? 0).toLocaleString() }))
            return
          }
          // #1397: the file's saved copy on the server is damaged — nothing was copied
          if (big && isContentDamagedCode(big.code)) {
            // (round 3 J6: its `.pmeta` companion's copy is the damaged one — named, never blamed on the document)
            // (K1: the server names the damaged file — the copied item when it names none)
            toast.error(big.companion
              ? t('shareView.copyDamagedCompanion', { name: big.name ?? clipboard.name, companion: big.companion })
              : t('shareView.copyDamaged', { name: big.name ?? clipboard.name }))
            return
          }
        }
        // Read the body before flattening it. The server answers a full drive
        // with 413 + STORAGE_QUOTA_EXCEEDED and the shared sentence; discarding
        // it left the recipient with "Copy failed" — indistinguishable from a
        // permission or network error, and on someone ELSE's drive, where the
        // generic wording gives them nothing to act on.
        toast.error(await isReservedNameRefusal(res) ? t('driveView.toast.reservedName') : await refusalOrDefault(res, clipboard.mode === 'copy' ? 'Copy failed' : 'Move failed'))
        // the server can't say which side was refused: re-read the source's folder AND the destination
        rowRefusedRef.current(res.status, clipboard.id)
        if (target !== rootFileId) rowRefusedRef.current(res.status, target)
        return
      }
      if (clipboard.mode === 'cut') {
        // #801: a SPLIT move leaves the folder (and its protected branches) where it was —
        // remove only the branches that moved; a replay moved nothing and changes nothing.
        // No undo in the share view, so a replay records nothing by construction; it
        // (and a split) says what happened.
        const body = (await res.json().catch(() => null)) as { split?: { replay?: boolean; moved?: string[]; skipped?: PartitionPreview } } | null
        const split = body?.split
        setRootItems(prev => split ? (split.moved ?? []).reduce(removeNode, prev) : removeNode(prev, clipboard.id))
        setClipboard(null)
        const msg = body ? moveResultMessage(t, body) : null
        if (msg) toast.info(msg)
      }
      // A View-link copy leaves out links the visitor may only view (owner ruling I15) — say so.
      if (clipboard.mode === 'copy') {
        const body = (await res.json().catch(() => null)) as { leftOut?: string[]; notCopied?: Array<{ name: string; code?: string; companion?: string }> } | null
        const n = body?.leftOut?.length ?? 0
        if (n > 0) toast.info(t('shareView.deadPointer.notCopied', { count: n }))
        // #1397: the rest of the folder was copied — say which files were not, and why
        const notCopied = body?.notCopied ?? []
        const damaged = notCopied.filter((k) => isContentDamagedCode(k.code) && !k.companion).map((k) => k.name)
        // (round 3 J6: a file left out for its damaged `.pmeta` companion names that companion)
        const companionDamaged = notCopied.filter((k) => isContentDamagedCode(k.code) && k.companion).map((k) => `${k.name} (${k.companion})`)
        const other = notCopied.filter((k) => !isContentDamagedCode(k.code)).map((k) => k.name)
        if (damaged.length > 0) toast.error(t('shareView.notCopied.damaged', { count: damaged.length, names: damaged.join(', ') }))
        if (companionDamaged.length > 0) toast.error(t('shareView.notCopied.companionDamaged', { count: companionDamaged.length, names: companionDamaged.join(', ') }))
        if (other.length > 0) toast.error(t('shareView.notCopied.unplaceable', { count: other.length, names: other.join(', ') }))
      }
      await reloadFolder(target)
    } catch {
      toast.error('Paste failed')
    }
  }, [clipboard, closeMenu, sessionId, targetFolderOf, reloadFolder, toast, t, partitionGate, loadPreview])

  const handleCreate = useCallback(async (node: TreeNode | null, kind: 'folder' | 'file') => {
    closeMenu()
    const target = targetFolderOf(node)
    const name = kind === 'folder'
      ? uniqueName('New Folder', '', siblingNames(target))
      : uniqueName('Untitled', '.txt', siblingNames(target))
    try {
      const res = await shareWriteFetch(sessionId, `${getApiBase()}/api/share-links/session/${sessionId}/${kind === 'folder' ? 'folder' : 'new-file'}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentId: target, name }),
      })
      if (declinedDisclosure(res)) return
      if (!res.ok) { toast.error(await isReservedNameRefusal(res) ? t('driveView.toast.reservedName') : kind === 'folder' ? 'Create folder failed' : 'Create file failed'); return }
      const body = (await res.json()) as { file?: { id: string; name: string } }
      await reloadFolder(target)
      // Drop straight into inline-rename on the new item, like the Drive.
      if (body.file) { setRenameDraft(body.file.name); setRenamingId(body.file.id) }
    } catch {
      toast.error('Create failed')
    }
  }, [closeMenu, sessionId, targetFolderOf, siblingNames, reloadFolder, toast])

  const handleUploadPick = useCallback((node: TreeNode | null, which: 'files' | 'folder') => {
    closeMenu()
    uploadTargetRef.current = targetFolderOf(node)
    ;(which === 'files' ? fileInputRef : folderInputRef).current?.click()
  }, [closeMenu, targetFolderOf])

  /** #1316 review round 2 — the names of the files an upload never sent because its session ended (null = none). */
  const [uploadStopped, setUploadStopped] = useState<string[] | null>(null)
  const handleUploadFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return
    const target = uploadTargetRef.current
    const list = Array.from(files)
    // #1316 review round 2 (C-M3): stopped at a session end the view outlives (another share session, a revoke the server
    // did not confirm) — say which files were never sent, in a notice the view renders (D4 clears toasts at the end).
    // The folder is re-read only while the view still reads the share session the upload went to.
    const stoppedAt = (from: number) => {
      const unsent = list.slice(from).map((f) => f.name)
      if (unsent.length > 0) setUploadStopped(unsent)
      if (!lastEndMovedShareSession()) void reloadFolder(target)
    }
    let failed = 0
    let queued = 0
    let storageMsg: string | null = null
    let replacingMsg: string | null = null
    let reservedMsg: string | null = null
    const queuedReplaces: Array<{ recordId: string; name: string }> = []
    // #1316 review round 1: the upload belongs to the share session it started in — the file in flight goes on, but no
    // further file (nor a landing retry) is sent once that session ended (`stoppedAt` says which)
    const start = currentSessionSignal()
    for (let i = 0; i < list.length; i++) {
      const file = list[i]!
      if (sessionEndedSince(start)) { stoppedAt(i); return }
      // For folder uploads webkitRelativePath is "root/sub/name"; the dir
      // (minus the leaf) becomes the mkdir -p `path` under the target.
      const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || ''
      const dir = rel.includes('/') ? rel.slice(0, rel.lastIndexOf('/')) : ''
      const qs = new URLSearchParams({ parentId: target })
      if (dir) qs.set('path', dir)
      try {
        const send = () => shareWriteFetch(sessionId, `${getApiBase()}/api/share-links/session/${sessionId}/upload?${qs.toString()}`, {
          method: 'POST', credentials: 'include',
          headers: { 'x-file-name': encodeURIComponent(file.name) },
          body: file,
        })
        let res = await send()
        // #1134 — a save of this database is still being written to disk (seconds): sent again, a few times.
        for (let attempt = 0; attempt < LANDING_RETRIES && res.status === 503 && (await answerOf(res))?.landing === true; attempt++) {
          await new Promise((r) => setTimeout(r, LANDING_RETRY_MS))
          if (sessionEndedSince(start)) { stoppedAt(i); return }   // this file's retry never went out
          res = await send()
        }
        // Keep the FIRST storage refusal: on a full drive every file fails for
        // the same reason, and "3 files failed to upload" names none of it.
        if (declinedDisclosure(res)) return
        if (!res.ok) {
          failed++
          // #1090 — the database is being replaced in the background (or #1134, a save of it is still being written):
          // said, never a bare "failed to upload".
          const answer = await answerOf(res)
          if (answer?.code === 'ENGINE_DB_REPLACING') {
            replacingMsg ??= answer.landing === true
              ? 'A save of this database is still being written — try the upload again in a moment'
              : 'This database is being replaced — upload it again when that finishes'
          }
          else if (answer?.code === 'RESERVED_NAME') reservedMsg ??= t('driveView.toast.reservedName')
          else storageMsg ??= await storageRefusalFrom(res)
        }
        // #1090 — a large app database is replaced in the background: the upload is done, the Replace follows.
        else if (res.status === 202) {
          queued++
          const recordId = (await answerOf(res))?.recordId
          if (recordId) queuedReplaces.push({ recordId, name: file.name })
        }
      } catch (err) {
        // this file was sent (its answer dropped — the re-read shows whether it landed); the rest were not
        if (sessionEndedSince(start, err)) { stoppedAt(i + 1); return }
        failed++
      }
    }
    if (failed > 0) {
      toast.error(storageMsg ?? replacingMsg ?? reservedMsg ?? `${failed} file${failed > 1 ? 's' : ''} failed to upload`)
    }
    if (queued > 0) toast.success('Uploaded — the database is being replaced in the background')
    await reloadFolder(target)
    // #1090 (review) — how each one ended, told here: a public-link visitor has no bell to receive it. Watched with the
    // session token that MADE the upload (the record is keyed to it; opening another file mints a new one).
    const uploadSession = sessionId
    for (const q of queuedReplaces) {
      void watchShareReplace({ apiBase: getApiBase(), sessionId: uploadSession, recordId: q.recordId, isActive: () => replaceWatchAliveRef.current })
        .then((op) => {
          if (!op || !replaceWatchAliveRef.current) return
          const key = dbOperationNoticeKey(op)
          if (!key) return
          const text = `“${q.name}”: ${tCollab(key)}`
          if (dbOperationNoticeTone(op) === 'success') toast.success(text, 10_000)
          else toast.warning(text, 15_000)
          if (op.outcome === 'replaced') void reloadFolder(target)
        })
        .catch(() => {})
    }
  }, [sessionId, reloadFolder, toast, tCollab])

  // ── Bin (deleted items) view — beside the Files tree, edit links only ──
  const [view, setView] = useState<'files' | 'bin'>('files')
  const [binItems, setBinItems] = useState<BinRow[]>([])
  const [binLoading, setBinLoading] = useState(false)

  const loadBin = useCallback(async () => {
    setBinLoading(true)
    try {
      const res = await sessionFetch(`${getApiBase()}/api/share-links/session/${sessionId}/bin`, { credentials: 'include' })
      setBinItems(res.ok ? ((await res.json()) as { items: BinRow[] }).items : [])
    } catch (err) {
      // #1316: a dropped answer (its session ended) is no "the Bin is empty" — the view keeps what it shows
      if (!isSessionEndedError(err)) setBinItems([])
    } finally { setBinLoading(false) }
  }, [sessionId])

  const showBin = useCallback(() => { setView('bin'); void loadBin() }, [loadBin])

  // Bin restore follows the Drive bin's contract: the target is the item's original
  // parent, or a folder the visitor picks. When the server can't restore there
  // (gone / moved out of the share / hidden / read-only for this visitor / inside an
  // app whose reach the link lacks) it answers 409 RESTORE_TARGET_UNAVAILABLE — never
  // a silent relocation to the share root — and the picker asks where it should go
  // instead. A pick the server refuses the same way re-opens the picker.
  const [restorePickerFor, setRestorePickerFor] = useState<{ binId: string; originalName: string } | null>(null)

  const handleRestore = useCallback(async (binId: string, originalName: string, targetParentId?: string) => {
    const signal = sessionSignal()
    try {
      // E7 PR 0b: the request belongs to the visit (`sessionSignal`): its Log out / a revoke aborts it in flight and an
      // answer after that is dropped. No automatic retry — a "still verifying" 503 says try again in a moment.
      const res = await restoreShareBinEntry(sessionId, binId, targetParentId, signal)
      if (signal.aborted) return
      if (declinedDisclosure(res)) return
      if (!res.ok) {
        const body = (await res.clone().json().catch(() => ({}))) as { error?: string; code?: string; min?: number }
        if (signal.aborted) return
        if (body.code === 'ENGINE_DB_VERIFYING') {
          // an app database in it is still being checked: nothing was done, it stays in the Bin
          toast.error(t('binView.toast.restoreDbVerifying'))
          return
        }
        if (body.code === TOO_LARGE_FOR_SHARE_VIEW_CODE) {
          toast.error(t('binView.toast.tooLargeForShareView', { name: originalName, min: (body.min ?? 0).toLocaleString() }))
          return
        }
        // #826: a folder inside the item is back in the drive elsewhere — no folder choice helps.
        if (body.error === 'restore_inner_folder_live') {
          toast.error(t('binView.toast.restoreInnerFolderLiveShare', { name: originalName }))
          return
        }
        // #826: it merges into a folder back in the drive that this link can't add to.
        if (body.error === 'restore_merge_refused') {
          toast.error(t('binView.toast.restoreMergeRefusedShare', { name: originalName }))
          return
        }
        // #826: a newer Bin copy of a folder in it must come back first.
        if (body.error === 'restore_newer_copy') {
          toast.error(t('binView.toast.restoreNewerCopyShare', { name: originalName }))
          return
        }
        // #826: something it merges into changed meanwhile — the same folder works on a retry.
        if (body.error === 'restore_changed') {
          toast.error(t('binView.toast.restorePlanChanged', { name: originalName }))
          return
        }
        if (res.status === 409 && body.error === 'RESTORE_TARGET_UNAVAILABLE') {
          toast.info(t('binView.sharePicker.needsTarget', { name: originalName }))
          setRestorePickerFor({ binId, originalName })
          return
        }
        // #934: it has access settings of its own — it goes back only where it was, no picker.
        if (res.status === 403 && body.error === 'restore_own_access_original_only') {
          toast.error(t('binView.sharePicker.ownAccessOriginalOnly', { name: originalName }))
          return
        }
        // An app's script deleted it: it goes back only where it was (#775) — no picker.
        if (res.status === 403 && body.error === 'restore_to_original_only') {
          toast.error(t('binView.sharePicker.originalOnly', { name: originalName }))
          return
        }
        const refusal = await refusalOrDefault(res, t('binView.toast.restoreFailed'))
        if (!signal.aborted) toast.error(refusal)
        return
      }
      const body = (await res.json()) as { file?: { parentId: string | null }; restoredIntoLive?: boolean }
      if (signal.aborted) return
      setBinItems(prev => prev.filter(b => b.id !== binId))
      toast.success(body.restoredIntoLive ? t('binView.toast.restoredIntoLive', { name: originalName }) : t('binView.toast.restored'))
      // Reflect the restored item back into the Files tree.
      if (body.file?.parentId) await reloadFolder(body.file.parentId)
    } catch { if (!signal.aborted) toast.error(t('binView.toast.restoreFailed')) }
  }, [sessionId, toast, reloadFolder, t])

  // Initial root listing + restore — pre-load every persisted-expanded
  // folder so the tree opens in the same shape the recipient left it,
  // even after a sub-file navigation remounts the explorer.
  // #1316 review round 1 (C-M1): the explorer outlives a share-session replacement of the same identity — a root listing
  // dropped at a session end is asked again (`rootReask`); never an unhandled rejection with the tree spinner left on.
  // Review round 2: asked again only as a session the view reads now — at an end that MOVES the share session the
  // `sessionId` prop still names the ended one until the new session lands, so the re-ask waits for it to change
  // (`useDroppedLoadReask`); an identity switch rebuilds the tree through `identityKey` anyway.
  const { reask: rootReask, begin: beginRootLoad } = useDroppedLoadReask(sessionId)
  useEffect(() => {
    let cancelled = false
    const asked = beginRootLoad()
    void (async () => {
      let tree: TreeNode[]
      try {
        tree = await loadFolder(rootFileId)
      } catch (err) {
        if (cancelled) return
        if (isSessionEndedError(err)) { asked.dropped(); return }
        setLoading(false)
        return
      }
      if (cancelled) return
      setRootItems(tree)
      setLoading(false)
      // Walk the restored expandedSet and lazy-load each folder's children so
      // the tree reopens in the shape the recipient left it.
      for (const id of expandedSet) {
        const restored = findNode(tree, id)
        // A pointer that went dead since it was expanded is not re-opened (greyed, not openable).
        if (!restored || restored.shareBroken || restored.chainBroken) continue
        try {
          const children = await loadFolder(id)
          if (cancelled) return
          tree = updateNode(tree, id, { children, loaded: true, loading: false })
          setRootItems(tree)
        } catch (err) {
          // #1316: dropped at a session end — leave it collapsed (as an expand's drop does), not open with no children
          if (!cancelled && isSessionEndedError(err)) {
            setExpandedSet(prev => { if (!prev.has(id)) return prev; const next = new Set(prev); next.delete(id); return next })
          }
        }
      }
    })()
    return () => { cancelled = true }
    // Re-runs on root change OR a recipient-identity switch (identityKey) — NOT on
    // a plain file switch (same identity, new token) and NOT on expandedSet changes
    // from user clicks, which would needlessly rebuild the whole tree.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFolder, rootFileId, identityKey, rootReask])

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Files / Bin switch — the Bin (deleted items + restore) is edit-only. */}
      {canEdit && (
        <div className="flex items-center gap-1 px-2 py-1 border-b themed-border shrink-0">
          {([
            { key: 'files' as const, label: 'Files', icon: FolderTree, onClick: () => setView('files') },
            { key: 'bin' as const, label: 'Bin', icon: Trash2, onClick: showBin },
          ]).map(t => (
            <button
              key={t.key}
              type="button"
              onClick={t.onClick}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ${view === t.key ? 'themed-bg-tertiary themed-text' : 'themed-text-secondary hover:themed-text'}`}
            >
              <t.icon size={13} />
              {t.label}
            </button>
          ))}
        </div>
      )}

      {uploadStopped && (
        <div role="status" className="mx-1 my-1 flex items-start gap-2 rounded px-2 py-1.5 text-xs themed-bg-tertiary themed-text">
          <span className="flex-1 break-words">
            {t('shareView.uploadStopped.message', { count: uploadStopped.length, names: uploadStopped.join(', ') })}
          </span>
          <button
            type="button"
            onClick={() => setUploadStopped(null)}
            className="shrink-0 themed-text-secondary hover:themed-text"
          >
            {t('shareView.uploadStopped.dismiss')}
          </button>
        </div>
      )}

      {view === 'bin' ? (
        <div className="flex-1 overflow-auto p-1 text-sm">
          {binLoading ? (
            <div className="flex items-center justify-center p-4"><Loader2 size={16} className="animate-spin themed-text-muted" /></div>
          ) : binItems.length === 0 ? (
            <div className="px-2 py-6 text-center text-xs themed-text-muted">Bin is empty</div>
          ) : (
            binItems.map(b => (
              <div key={b.id} className="group flex items-center gap-1 py-1 px-1 rounded hover:themed-bg-tertiary">
                {/* Bin rows carry no metadata_json → miniapp folders render
                    the name-derived default icon. */}
                <FileIcon name={b.name} isFolder={b.isFolder} size={14} miniappIconName={b.name} />
                <span className="truncate flex-1 themed-text-secondary">{b.name}</span>
                <button
                  type="button"
                  onClick={() => handleRestore(b.id, b.name)}
                  title="Restore"
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] themed-text-secondary hover:themed-text hover:themed-bg-secondary opacity-0 group-hover:opacity-100"
                >
                  <RotateCcw size={12} />
                  Restore
                </button>
              </div>
            ))
          )}
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center p-4">
          <Loader2 size={16} className="animate-spin themed-text-muted" />
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-1 text-sm" onContextMenu={handleBackgroundContextMenu}>
          {rootItems.map(item => (
            <TreeRow
              key={item.id}
              node={item}
              depth={0}
              activeFileId={activeFileId}
              expandedSet={expandedSet}
              onToggleExpand={toggleExpand}
              onFileOpen={onFileOpen}
              onLoad={handleLoad}
              onOpenPdapp={onOpenPdapp}
              onContextMenu={handleContextMenu}
              renamingId={renamingId}
              renameDraft={renameDraft}
              onRenameDraft={setRenameDraft}
              onRenameCommit={commitRename}
              onRenameCancel={cancelRename}
            />
          ))}
        </div>
      )}

      {linkedDownload && (
        <FolderLinkedDownloadDialog
          folderName={linkedDownload.name}
          themeClass={themeClass}
          t={t}
          // The share view preselects CONTENTS (owner ruling 2026-09-27); the drive keeps pointers.
          defaultChoice="contents"
          onCancel={() => setLinkedDownload(null)}
          onConfirm={(choice) => { const node = linkedDownload; setLinkedDownload(null); startFolderDownload(node, choice === 'contents') }}
        />
      )}
      {restorePickerFor && (
        <ShareRestoreTargetPicker
          originalName={restorePickerFor.originalName}
          rootFileId={rootFileId}
          rootPermission={rootPermission}
          listFolder={loadFolder}
          onCancel={() => setRestorePickerFor(null)}
          onSelect={(folderId) => {
            const target = restorePickerFor
            setRestorePickerFor(null)
            void handleRestore(target.binId, target.originalName, folderId)
          }}
        />
      )}

      {/* Hidden upload inputs (driven by the menu's Upload items). */}
      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => { void handleUploadFiles(e.target.files); e.target.value = '' }} />
      <input ref={folderInputRef} type="file" className="hidden" {...({ webkitdirectory: '', directory: '' } as Record<string, string>)} onChange={(e) => { void handleUploadFiles(e.target.files); e.target.value = '' }} />

      {menu && (
        // The SAME context-menu component the Drive/workspace explorer uses. The
        // share-view offers the full set MINUS the owner-only governance actions
        // (AI Access, Who-has-access, Reveal in Drive, Link Drive Folder).
        (() => {
          const node = menu.node
          // Write affordances gate on the EFFECTIVE permission of the exact target,
          // not a tree-wide flag: a recipient with native write on only some files
          // sees Rename/Delete enabled only there. Node ops (Rename/Copy/Cut/Delete)
          // gate on the node's own perm; create/paste/upload gate on the DESTINATION
          // folder's perm (a file node → its parent; the background → the share root).
          const roNode = !canWritePerm(node?.effectivePermission)
          const roScope = !canWritePerm(folderPermOf(targetFolderOf(node)))
          const items: FileMenuItem[] = []
          if (node) {
            items.push({ kind: 'item', icon: Pencil, label: 'Rename', shortcut: 'F2', disabled: roNode, onClick: () => startRename(node) })
            items.push({ kind: 'item', icon: Copy, label: 'Copy', disabled: roNode, onClick: () => handleCopyCut(node, 'copy') })
            items.push({ kind: 'item', icon: Scissors, label: 'Cut', disabled: roNode, onClick: () => handleCopyCut(node, 'cut') })
          }
          if (clipboard) items.push({ kind: 'item', icon: ClipboardPaste, label: clipboard.mode === 'cut' ? `Move "${clipboard.name}" here` : `Paste "${clipboard.name}"`, disabled: roScope, onClick: () => handlePaste(node) })
          if (node && !node.isFolder) {
            items.push({ kind: 'separator' })
            items.push({ kind: 'item', icon: ExternalLink, label: 'Open in New Tab', onClick: () => handleOpenNewTab(node) })
            items.push({ kind: 'item', icon: Download, label: 'Download', onClick: () => handleDownload(node) })
          }
          if (node && node.isFolder) {
            items.push({ kind: 'separator' })
            items.push({ kind: 'item', icon: Download, label: 'Download as Zip', onClick: () => handleDownloadFolder(node) })
          }
          if (node) {
            items.push({ kind: 'separator' })
            items.push({ kind: 'item', icon: Trash2, label: 'Delete', shortcut: '⌫', danger: true, disabled: roNode, onClick: () => handleDelete(node) })
          }
          items.push({ kind: 'separator' })
          items.push({ kind: 'item', icon: FolderPlus, label: 'Create folder', disabled: roScope, onClick: () => handleCreate(node, 'folder') })
          items.push({ kind: 'item', icon: FilePlus, label: 'New Text File', disabled: roScope, onClick: () => handleCreate(node, 'file') })
          items.push({ kind: 'item', icon: Upload, label: 'Upload files...', disabled: roScope, onClick: () => handleUploadPick(node, 'files') })
          items.push({ kind: 'item', icon: FolderUp, label: 'Upload folder...', disabled: roScope, onClick: () => handleUploadPick(node, 'folder') })
          return (
            <FileContextMenu
              x={menu.x}
              y={menu.y}
              menuRef={menuRef}
              className={themeClass}
              testId="share-explorer-context-menu"
              items={items}
            />
          )
        })()
      )}

    </div>
  )
}
